import {createSnackbar} from 'Core/ducks/snackbar';
import {Async, AsyncResult, Loaded, Loading, ServerError, isLoaded} from 'Core/models/Async';
import ProposalAPI from 'Quoting/api/ProposalAPI';
import QuotingAPI from 'Quoting/api/QuotingAPI';
import {
  QuotingActionType,
  changeAvailableQuoteActions,
  changeOpportunityQuoteList,
  changeOpportunitySummary,
  getAvailableQuoteActions,
  getOpportunitySummary,
  getQuotesForProposal,
  updateOpportunityProposals,
  updateQuotesLinkedToProposals,
} from 'Quoting/ducks/opportunity';
import {A360ProductCd, A360ProductModel} from 'Quoting/models/Quoting';
import {AvailableQuoteActions, OpportunityQuote, OpportunitySummaryDto} from 'Quoting/models/DTOs/Opportunity';
import {Proposal, ProposalQuoteDetails} from 'Quoting/models/DTOs/Proposal';
import {getProductCdFromModel} from 'Quoting/utils/helpers';
import {fold, isRight} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';
import {all, call, put, select, takeLatest} from 'redux-saga/effects';
import {retreivePoliciesWithRates} from './commonQuotingSagaMethods';
import {ReducerState} from 'rootReducer';
import {DateTime} from 'luxon';
import {LocalStorageKey, toSessionStorage} from 'Shared/utilities/localStorage';

const getQuotesData = (opportunityQuoteList: OpportunityQuote[]) =>
  opportunityQuoteList?.map(quote => ({
    rootId: quote.entity._key.rootId,
    productCd: getProductCdFromModel(quote.entity.productCd as A360ProductModel) as A360ProductCd,
  }));

export function* fetchOpportunitySummaryFromAPI(action: ReturnType<typeof getOpportunitySummary>) {
  const errorMessage = 'Error: Could not fetch opportunity details!';
  try {
    yield put(changeOpportunitySummary(new Loading()));

    const response: AsyncResult<OpportunitySummaryDto> = yield call(
      QuotingAPI.getOpportunitySummary,
      action.payload.opportunityId
    );

    if (isRight(response) && response.right?._key?.rootId) {
      yield all([
        call(fetchOpportunityQuoteListFromAPI, response.right?._key?.rootId, response.right?._key?.revisionNo),
        call(fetchProposalData, response.right?._key?.rootId, response.right?.customerNumber),
      ]);
      toSessionStorage(LocalStorageKey.CustomerName)(response.right?.customerName)();
      yield put(changeOpportunitySummary(new Loaded(response.right)));
    } else {
      yield put(changeOpportunitySummary(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    yield put(changeOpportunitySummary(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  }
}

function* fetchOpportunitySummarySaga() {
  yield takeLatest(QuotingActionType.GET_OPPORTUNITY_SUMMARY, fetchOpportunitySummaryFromAPI);
}

const sortQuotes = (quotes: OpportunityQuote[]): OpportunityQuote[] =>
  quotes.sort((a, b) => {
    if (!a?.entity?.updatedOn) {
      return -1;
    }
    if (!b?.entity?.updatedOn) {
      return 1;
    }
    const updatedOnDateA = DateTime.fromISO(a?.entity?.updatedOn);
    const updatedOnDateB = DateTime.fromISO(b?.entity?.updatedOn);

    return updatedOnDateB.diff(updatedOnDateA).milliseconds;
  });

export function* fetchOpportunityQuoteListFromAPI(opportunityRootId: string, opportunityRevisionNo: number) {
  const errorMessage = 'Error: Could not fetch opportunity details!';
  try {
    yield put(changeOpportunityQuoteList(new Loading()));

    const response: AsyncResult<OpportunityQuote[]> = yield call(
      QuotingAPI.getOpportunityQuotesList,
      opportunityRootId,
      opportunityRevisionNo
    );
    if (isRight(response)) {
      if (!Array.isArray(response.right)) {
        yield put(changeOpportunityQuoteList(new Loaded([])));
      } else {
        yield put(changeOpportunityQuoteList(new Loaded(sortQuotes(response.right))));
        const quotesDataAvailableActionsPayload: {rootId: string; productCd: A360ProductCd}[] = yield call(
          getQuotesData,
          response.right
        );
        yield put(getAvailableQuoteActions(quotesDataAvailableActionsPayload));
      }
    } else {
      yield put(changeOpportunityQuoteList(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    yield put(changeOpportunityQuoteList(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  }
}

function* fetchAvailableQuoteActions(action: ReturnType<typeof getAvailableQuoteActions>) {
  const errorMessage = 'Error: could not fetch available quote actions!';
  try {
    const fetchQuoteActions = action.payload.quotes.map(quote =>
      call(QuotingAPI.getAvailableQuoteActions, quote.productCd, quote.rootId)
    );

    yield put(changeAvailableQuoteActions(new Loading()));

    const responses: Array<AsyncResult<AvailableQuoteActions[]>> = yield all(fetchQuoteActions);

    const availableActionsForQuoteList: Record<string, AvailableQuoteActions[]> = pipe(
      responses,
      responsesData =>
        responsesData.map(response =>
          fold(
            () => [],
            (right: AvailableQuoteActions[]) => right
          )(response)
        ),
      listOfQuoteActions =>
        listOfQuoteActions.reduce((acc: Record<string, any>, cur, index) => {
          acc[action.payload.quotes[index].rootId] = cur;
          return acc;
        }, {})
    );

    yield put(changeAvailableQuoteActions(new Loaded(availableActionsForQuoteList)));
  } catch (error) {
    console.error(error)
    yield put(changeAvailableQuoteActions(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  }
}

function* fetchAvailableQuoteActionsSaga() {
  yield takeLatest(QuotingActionType.GET_AVAILABLE_QUOTE_ACTIONS, fetchAvailableQuoteActions);
}

function* fetchProposalData(opportunityRootId: string, customerNumber: string) {
  const errorMessage = 'Error: Could not fetch proposal details!';

  try {
    yield put(updateOpportunityProposals(new Loading()));
    const response: AsyncResult<Proposal[]> = yield call(
      ProposalAPI.getProposalList,
      opportunityRootId,
      customerNumber
    );

    if (isRight(response)) {
      yield put(updateOpportunityProposals(new Loaded(response.right)));
    } else {
      yield put(updateOpportunityProposals(new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    yield put(updateOpportunityProposals(new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  }
}

function* fetchQuotesLinkedWithProposal(action: ReturnType<typeof getQuotesForProposal>) {
  const errorMessage = 'Error: could not fetch quotes linked to proposal!';
  try {
    yield put(updateQuotesLinkedToProposals(action.payload.proposalNumber, new Loading()));

    const quoteLinks = action.payload.proposalQuotes.map(q => q._uri);

    const response: AsyncResult<ProposalQuoteDetails[]> = yield call(retreivePoliciesWithRates, quoteLinks, true);

    if (isRight(response)) {
      yield put(updateQuotesLinkedToProposals(action.payload.proposalNumber, new Loaded(response.right)));
    } else {
      yield put(updateQuotesLinkedToProposals(action.payload.proposalNumber, new ServerError(errorMessage)));
      yield put(createSnackbar(errorMessage));
    }
  } catch (error) {
    yield put(updateQuotesLinkedToProposals(action.payload.proposalNumber, new ServerError(errorMessage)));
    yield put(createSnackbar(errorMessage));
  }
}

function* fetchQuotesLinkedWithProposalSaga() {
  yield takeLatest(QuotingActionType.GET_QUOTES_FOR_PROPOSAL, fetchQuotesLinkedWithProposal);
}

function* refreshProposalList() {
  try {
    const opportunitySummary: Async<OpportunitySummaryDto> = yield select(
      (state: ReducerState) => state.opportunity.opportunitySummary
    );
    if (isLoaded(opportunitySummary)) {
      yield call(fetchProposalData, opportunitySummary.Data?._key?.rootId, opportunitySummary.Data?.customerNumber);
    }
  } catch (error) {}
}

function* refreshProposalListSaga() {
  yield takeLatest(QuotingActionType.GET_OPPORUNITY_PROPOSALS, refreshProposalList);
}

export default function*() {
  yield all([
    fetchOpportunitySummarySaga(),
    fetchAvailableQuoteActionsSaga(),
    fetchQuotesLinkedWithProposalSaga(),
    refreshProposalListSaga(),
  ]);
}
